import type { Media } from '@prisma/client'
import { useCallback, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFileUploadContext } from '~/contexts/FileUploadContext'
import { validateUploadFileType } from '~/helpers/media'
import { makeShopOwnPath } from '~/helpers/routes'
import { useShop } from '~/hooks/data/use-shop'
import { MAX_FILE_UPLOAD } from '~/utils/constants'
import {
  FileUploadCode,
  type ImageKitUploadedFile,
  type FileUploadCode as SingleFileUploadCode,
} from '~/utils/file-upload'
import { getFileUploadTranslationKey } from '~/utils/file-upload'

export type UploadOptions = {
  fileName: string
  folder?: string
  tags?: string
}

export interface FileUploadProgress {
  fileId: string
  fileName: string
  progress: number // 0-100
  status: 'uploading' | 'saving' | 'completed'
  error?: string
  media?: Media
  abortController?: AbortController
  // For optimistic UI - temporary media card data
  optimisticMedia?: {
    id: string
    src: string
    kind: 'image' | 'video'
    thumbnailUrl?: string
  }
}

export interface IndividualUploadResult {
  success: boolean
  media?: Media
  error?: string
  errorCode?: SingleFileUploadCode
}

export type ImageKitUploadResult = {
  src: string
  kind: 'image' | 'video'
  extraFields: ImageKitUploadedFile
}

export const useFileUpload = () => {
  const { setUploadState, getUploadState, clearError } = useFileUploadContext()
  const { t } = useTranslation()
  const [uploadProgress, setUploadProgress] = useState<FileUploadProgress[]>([])
  const generateFileId = useId()
  const shop = useShop()

  const uploadFile = async (
    uploadId: string,
    files: FileList | null,
    uploadOptions: UploadOptions,
    allowedMediaKinds?: ('image' | 'video')[]
  ): Promise<Media[]> => {
    if (!files || files.length === 0) return []

    // Initialize upload state for this instance
    setUploadState(uploadId, { isUploading: true, error: null })

    try {
      if (!files?.length) return []

      // Check file count limit
      if (files.length > MAX_FILE_UPLOAD) {
        const errorMessage = getFileUploadTranslationKey(
          FileUploadCode.TOO_MANY_FILES
        )
        setUploadState(uploadId, {
          isUploading: false,
          error: t(errorMessage),
        })
        return []
      }

      // Initialize progress for all files
      const initialProgress: FileUploadProgress[] = Array.from(files).map(
        (file, index) => ({
          fileId: `${generateFileId}-${index}`,
          fileName: file.name,
          progress: 0,
          status: 'uploading',
          abortController: new AbortController(),
        })
      )

      setUploadProgress(initialProgress)

      // Upload files individually in parallel
      const uploadPromises = Array.from(files).map((file, index) => {
        const fileId = initialProgress[index]?.fileId
        if (!fileId)
          return Promise.resolve({ success: false, error: 'Invalid file ID' })

        // Validate file before uploading
        const validation = validateUploadFileType(file, t, allowedMediaKinds)
        if (!validation.success) {
          // Remove from progress if validation fails
          setUploadProgress((prev) => prev.filter((p) => p.fileId !== fileId))
          return Promise.resolve({ success: false, error: validation.error })
        }

        const abortController = initialProgress[index]?.abortController
        return uploadSingleFile(file, uploadOptions, fileId, abortController)
      })

      // Wait for all uploads to complete
      const results = await Promise.allSettled(uploadPromises)

      // Collect successful uploads and check for aborted uploads
      const successfulUploads = results
        .filter(
          (result) => result.status === 'fulfilled' && result.value.success
        )
        .map(
          (result) =>
            (result as PromiseFulfilledResult<IndividualUploadResult>).value
              .media!
        )
        .filter(Boolean)

      // Check if any uploads were aborted
      const hasAbortedUploads = results.some(
        (result) =>
          result.status === 'fulfilled' && result.value.error === 'aborted'
      )

      if (hasAbortedUploads) {
        // If uploads were aborted, set the specific error message
        setUploadState(uploadId, {
          isUploading: false,
          error: t('errors.file_upload_aborted'),
        })
      } else if (successfulUploads.length === 0) {
        setUploadState(uploadId, {
          isUploading: false,
          error: t(getFileUploadTranslationKey(FileUploadCode.UNKNOWN_ERROR)),
        })
      } else {
        setUploadState(uploadId, {
          isUploading: false,
          error: null,
        })
      }

      return successfulUploads
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Upload failed')

      console.error('Upload failed:', error)
      setUploadState(uploadId, {
        isUploading: false,
        error: t(getFileUploadTranslationKey(FileUploadCode.UNKNOWN_ERROR)),
      })
      return []
    }
  }

  // Progress management functions
  const updateFileProgress = useCallback(
    (fileId: string, updates: Partial<FileUploadProgress>) => {
      setUploadProgress((prev) =>
        prev.map((file) =>
          file.fileId === fileId ? { ...file, ...updates } : file
        )
      )
    },
    []
  )

  const clearUploadProgress = useCallback(() => {
    setUploadProgress([])
  }, [])

  // Single file upload with progress tracking
  const uploadSingleFile = async (
    file: File,
    uploadOptions: UploadOptions,
    fileId: string,
    abortController?: AbortController
  ): Promise<IndividualUploadResult> => {
    try {
      // Update status to uploading
      updateFileProgress(fileId, { status: 'uploading', progress: 0 })

      // Upload to ImageKit with progress tracking
      const imageKitResult = await uploadToImageKit(
        file,
        uploadOptions,
        fileId,
        abortController
      )

      if (!imageKitResult.success) {
        if (imageKitResult.aborted) {
          // Remove from progress if upload was aborted
          setUploadProgress((prev) => prev.filter((p) => p.fileId !== fileId))
          return { success: false, error: 'aborted' }
        }

        const errorMessage = t(
          getFileUploadTranslationKey(FileUploadCode.UNKNOWN_ERROR)
        )
        // Remove from progress if ImageKit upload fails
        setUploadProgress((prev) => prev.filter((p) => p.fileId !== fileId))
        return { success: false, error: errorMessage }
      }

      // Update status to saving
      updateFileProgress(fileId, { status: 'saving', progress: 90 })

      // Save to database using single file API
      const dbResult = await saveSingleFileToDb(imageKitResult.data!)

      if (!dbResult.success) {
        const errorMessage =
          dbResult.error ||
          t(getFileUploadTranslationKey(FileUploadCode.UNKNOWN_ERROR))
        // Remove from progress if database save fails
        setUploadProgress((prev) => prev.filter((p) => p.fileId !== fileId))
        return { success: false, error: errorMessage }
      }

      // Complete
      updateFileProgress(fileId, {
        status: 'completed',
        progress: 100,
        media: dbResult.media,
      })

      return { success: true, media: dbResult.media }
    } catch (error) {
      const errorMessage = t(
        getFileUploadTranslationKey(FileUploadCode.UNKNOWN_ERROR)
      )
      // Remove from progress if unknown error occurs
      setUploadProgress((prev) => prev.filter((p) => p.fileId !== fileId))
      return { success: false, error: errorMessage }
    }
  }

  // Upload to ImageKit with progress tracking
  const uploadToImageKit = async (
    file: File,
    uploadOptions: UploadOptions,
    fileId: string,
    abortController?: AbortController
  ): Promise<{
    success: boolean
    data?: ImageKitUploadResult
    aborted?: boolean
  }> => {
    return new Promise((resolve) => {
      const formData = new FormData()
      formData.append('files', file)
      formData.append('fileName', uploadOptions.fileName)
      if (uploadOptions.folder) {
        formData.append('folder', uploadOptions.folder)
      }

      const xhr = new XMLHttpRequest()

      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 80) // 0-80% for ImageKit upload
          updateFileProgress(fileId, { progress })
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText)
          if (
            response.success &&
            response.uploaded &&
            response.uploaded.length > 0
          ) {
            const uploadedFile = response.uploaded[0]
            resolve({
              success: true,
              data: {
                src: uploadedFile.filePath,
                kind: uploadedFile.fileType === 'image' ? 'image' : 'video',
                extraFields: uploadedFile,
              },
            })
          } else {
            resolve({ success: false })
          }
        } else {
          resolve({ success: false })
        }
      })

      xhr.addEventListener('error', () => {
        resolve({ success: false })
      })

      xhr.addEventListener('abort', () => {
        resolve({ success: false, aborted: true })
      })

      // Handle abort signal
      if (abortController) {
        abortController.signal.addEventListener('abort', () => {
          xhr.abort()
        })
      }

      xhr.open('POST', makeShopOwnPath(shop, '/imagekit'))
      xhr.send(formData)
    })
  }

  // Save single file to database
  const saveSingleFileToDb = async (
    imageKitData: ImageKitUploadResult
  ): Promise<IndividualUploadResult> => {
    try {
      const formData = new FormData()
      formData.append('file', JSON.stringify(imageKitData))

      const response = await fetch(makeShopOwnPath(shop, '/medias'), {
        method: 'POST',
        body: formData,
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        return {
          success: false,
          error:
            result.message ||
            t(getFileUploadTranslationKey(FileUploadCode.UNKNOWN_ERROR)),
        }
      }

      return {
        success: true,
        media: result.media,
      }
    } catch (error) {
      return {
        success: false,
        error: t(getFileUploadTranslationKey(FileUploadCode.UNKNOWN_ERROR)),
      }
    }
  }

  // Abort a specific file upload
  const abortFileUpload = useCallback((fileId: string) => {
    setUploadProgress((prev) => {
      const updated = prev.map((progress) => {
        if (progress.fileId === fileId && progress.abortController) {
          progress.abortController.abort()
          return { ...progress, status: 'completed' as const, progress: 0 }
        }
        return progress
      })
      return updated.filter((p) => p.fileId !== fileId)
    })
  }, [])

  // Abort all file uploads
  const abortAllUploads = useCallback(() => {
    setUploadProgress((prev) => {
      prev.forEach((progress) => {
        if (progress.abortController) {
          progress.abortController.abort()
        }
      })
      return []
    })
  }, [])

  return {
    uploadFile,
    getUploadState,
    clearError,
    uploadProgress,
    updateFileProgress,
    clearUploadProgress,
    uploadSingleFile,
    abortFileUpload,
    abortAllUploads,
  }
}
